/**
 * 搜索图片相关工具函数
 */

/**
 * 生成带有选框的缩略图
 * @param {string} originalRegion - 选框坐标，格式为 "x1,x2,y1,y2"
 * @param {string} url - 图片URL
 * @returns {Promise<string>} 带有选框的图片的base64数据
 */
export async function generateThumbnail(originalRegion, url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {

      // 解析选框坐标
      const coords = originalRegion.split(",");
      if (!coords || coords.length !== 4) {
        resolve('');
        return;
      }

      const [x1, x2, y1, y2] = coords.map(Number);
      const region = {
        x: x1,
        y: y1,
        width: x2 - x1,
        height: y2 - y1
      };

      // 创建临时 Canvas 用于生成带有选框的图片
      const canvasWithSelection = document.createElement('canvas');
      const ctxWithSelection = canvasWithSelection.getContext('2d');

      // 设置 Canvas 尺寸（与显示尺寸一致，避免资源浪费）
      const maxWidth = 500;
      const maxHeight = 500;
      canvasWithSelection.width = maxWidth;
      canvasWithSelection.height = maxHeight;

      // 禁用抗锯齿，使线条更清晰
      ctxWithSelection.imageSmoothingEnabled = false;
      ctxWithSelection.webkitImageSmoothingEnabled = false;
      ctxWithSelection.msImageSmoothingEnabled = false;

      // 计算缩放比例
      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const scale = Math.min(scaleX, scaleY);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      // 清空画布
      ctxWithSelection.clearRect(0, 0, maxWidth, maxHeight);

      // 绘制背景
      ctxWithSelection.fillStyle = '#f9f9f9';
      ctxWithSelection.fillRect(0, 0, maxWidth, maxHeight);

      // 绘制完整的原图
      ctxWithSelection.drawImage(img, 0, 0, scaledWidth, scaledHeight);

      // 计算选框在缩放后图片上的坐标
      const scaledRegionX = Math.round(region.x * scale);
      const scaledRegionY = Math.round(region.y * scale);
      const scaledRegionWidth = Math.round(region.width * scale);
      const scaledRegionHeight = Math.round(region.height * scale);

      // 确保坐标有效
      const validX = Math.max(0, scaledRegionX);
      const validY = Math.max(0, scaledRegionY);
      const validWidth = Math.min(scaledRegionWidth, maxWidth - validX);
      const validHeight = Math.min(scaledRegionHeight, maxHeight - validY);

      // 确保选框尺寸有效
      if (validWidth <= 0 || validHeight <= 0) {
        resolve('');
        return;
      }

      // 在原图上绘制选框
      // 先绘制半透明背景
      ctxWithSelection.fillStyle = 'rgba(1, 106, 248, 0.2)';
      ctxWithSelection.fillRect(
        validX,
        validY,
        validWidth,
        validHeight
      );
      // 再绘制实线边框
      ctxWithSelection.strokeStyle = '#0056b3';
      ctxWithSelection.lineWidth = 2.5;
      ctxWithSelection.strokeRect(
        Math.round(validX),
        Math.round(validY),
        Math.round(validWidth),
        Math.round(validHeight)
      );

      // 获取带有选框的图片的 base64 数据
      const imageWithSelectionBase64 = canvasWithSelection.toDataURL('image/png');
      resolve(imageWithSelectionBase64);
    };

    // 处理图片加载失败的情况
    img.onerror = () => {
      console.error('图片加载失败:', url);
      resolve('');
    };

    // 设置图片源
    img.src = url;
  });
}

/**
 * 生成带有选框的小图片列表
 * @param {Object} selectImage - 包含图片信息和选框坐标的对象
 * @param {string} selectImage.base64 - 原图的base64数据
 * @param {Array<string>} selectImage.multiRegion - 多个选框的坐标数组，每个坐标格式为 "x1,x2,y1,y2"
 * @param {string} selectImage.key - 图片的唯一标识
 * @returns {Promise<Array>} 带有选框的小图片列表
 */
export async function generateSmallImages(selectImage) {
  return new Promise((resolve, reject) => {
    if (!selectImage || !selectImage.base64) {
      resolve([]);
      return;
    }

    const base64 = selectImage.base64;
    const multiRegion = selectImage.multiRegion;
    const smallImages = [];

    // 处理多个选框
    if (multiRegion && Array.isArray(multiRegion) && multiRegion.length > 0) {

      const img = new Image();
      img.onload = () => {
        // 循环处理 multiRegion 数组中的每个选框
        for (let i = 0; i < multiRegion.length; i++) {
          const regionStr = multiRegion[i];

          // 解析选框坐标
          const coords = regionStr.split(",");
          if (!coords || coords.length !== 4) {
            continue;
          }

          const [x1, x2, y1, y2] = coords.map(Number);
          const region = {
            x: x1,
            y: y1,
            width: x2 - x1,
            height: y2 - y1
          };

          // 创建临时 Canvas 用于生成带有选框的图片
          const canvasWithSelection = document.createElement('canvas');
          const ctxWithSelection = canvasWithSelection.getContext('2d');

          // 设置 Canvas 尺寸（与显示尺寸一致，避免资源浪费）
          const maxWidth = 500;
          const maxHeight = 500;
          canvasWithSelection.width = maxWidth;
          canvasWithSelection.height = maxHeight;

          // 禁用抗锯齿，使线条更清晰
          ctxWithSelection.imageSmoothingEnabled = false;
          ctxWithSelection.webkitImageSmoothingEnabled = false;
          ctxWithSelection.msImageSmoothingEnabled = false;

          // 计算缩放比例
          const scaleX = maxWidth / img.width;
          const scaleY = maxHeight / img.height;
          const scale = Math.min(scaleX, scaleY);
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;

          // 清空画布
          ctxWithSelection.clearRect(0, 0, maxWidth, maxHeight);

          // 绘制背景
          ctxWithSelection.fillStyle = '#f9f9f9';
          ctxWithSelection.fillRect(0, 0, maxWidth, maxHeight);

          // 绘制完整的原图
          ctxWithSelection.drawImage(img, 0, 0, scaledWidth, scaledHeight);

          // 计算选框在缩放后图片上的坐标
          const scaledRegionX = Math.round(region.x * scale);
          const scaledRegionY = Math.round(region.y * scale);
          const scaledRegionWidth = Math.round(region.width * scale);
          const scaledRegionHeight = Math.round(region.height * scale);

          // 确保坐标有效
          const validX = Math.max(0, scaledRegionX);
          const validY = Math.max(0, scaledRegionY);
          const validWidth = Math.min(scaledRegionWidth, maxWidth - validX);
          const validHeight = Math.min(scaledRegionHeight, maxHeight - validY);

          // 确保选框尺寸有效
          if (validWidth <= 0 || validHeight <= 0) {
            continue;
          }

          // 在原图上绘制选框
          // 先绘制半透明背景
          ctxWithSelection.fillStyle = 'rgba(1, 106, 248, 0.2)';
          ctxWithSelection.fillRect(
            validX,
            validY,
            validWidth,
            validHeight
          );
          // 再绘制实线边框
          ctxWithSelection.strokeStyle = '#0056b3';
          ctxWithSelection.lineWidth = 2.5;
          ctxWithSelection.strokeRect(
            Math.round(validX),
            Math.round(validY),
            Math.round(validWidth),
            Math.round(validHeight)
          );

          // 获取带有选框的图片的 base64 数据
          const imageWithSelectionBase64 = canvasWithSelection.toDataURL('image/png');

          // 添加到数组
          smallImages.push({
            id: Date.now() + i + Math.random(),
            checked: false,
            region: regionStr,
            key: selectImage.key,
            base64: imageWithSelectionBase64
          });
        }

        // 返回生成的小图片列表
        resolve(smallImages);
      };

      // 处理图片加载失败的情况
      img.onerror = () => {
        console.error('图片加载失败:', base64);
        resolve([]);
      };

      // 设置图片源
      img.src = base64;
    } else {
      console.log("没有多个选框数据");
      resolve([]);
    }
  });
}

/**
 * 模块导出
 */
export default {
  generateThumbnail,
  generateSmallImages
};